import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Input } from 'components/ui/input';
import { useRef, useState } from 'react';
import { BadgeAlert, Upload, Loader } from 'lucide-react';
import axiosInstance from 'api/axios';

const Chatwithkb = ({
  chatwkbOpen,
  setchatwkbOpen,
  botId,
  services,
}: {
  chatwkbOpen: boolean;
  setchatwkbOpen: (value: boolean) => void;
  botId: string;
  services: number[];
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadedDoc, setUploadedDoc] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInput = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Only PDF, DOC, and DOCX are allowed.');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setError('File size exceeds 20MB limit.');
        return;
      }
      setError(null);
      setUploadedDoc(file);
    }
  };

  const handleSave = async (id: string) => {
    console.log('tt');

    if (!uploadedDoc) {
      setError('No file uploaded.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedDoc);
      console.log(formData);

      console.log('ttt');

      const response = await axiosInstance.image.put(
        `/api/v1/tele/bots/chatwkb/${id}?services=${services.join(',')}`,
        formData
      );

      console.log('File uploaded successfully:', response.data);
      setchatwkbOpen(false); // Close the dialog
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog.Root open={chatwkbOpen} onOpenChange={setchatwkbOpen}>
      <Dialog.Overlay className="fixed inset-0 bg-black/50" />

      <Dialog.Content className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-lg shadow-lg p-4 sm:p-9 flex flex-col gap-4 sm:gap-6 bg-[#EEF1F0] w-[90%] sm:w-[550px] max-h-[90vh] overflow-y-auto">
        <Dialog.Title className="text-xl sm:text-2xl text-left font-bold text-[#1A211E]">
          Chat with KaiBot
        </Dialog.Title>

        <div className="space-y-2">
          <h3 className="font-bold text-[14px]">Advanced configuration</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label
                htmlFor="usageLimit"
                className="text-[14px] text-[#1C2024] font-normal flex gap-1 items-center"
              >
                Usage limit
                <BadgeAlert size={15} className="cursor-pointer" />
              </label>
              <Input
                id="usageLimit"
                className="border-2 border-[#D7DAD9] bg-[#ffffff] text-[#5F6563] focus:ring-0 focus:outline-none focus:border-[#D7DAD9] w-full"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="resetPeriod"
                className="text-[14px] text-[#1C2024] font-normal flex gap-1 items-center"
              >
                Reset period
                <BadgeAlert size={15} className="cursor-pointer" />
              </label>
              <Select>
                <SelectTrigger
                  id="resetPeriod"
                  className="border-2 border-[#D7DAD9] bg-[#ffffff] text-[#5F6563] w-full"
                >
                  <SelectValue placeholder="Select reset period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DAILY">Daily</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                  <SelectItem value="YEARLY">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="w-full h-[1px] bg-input my-2 sm:my-3" />

        <label htmlFor="fileUpload" className="hidden">
          Upload File
        </label>
        <input
          id="fileUpload"
          type="file"
          className="hidden"
          ref={inputRef}
          onChange={handleFileChange}
        />
        <div className="space-y-2 sm:space-y-3">
          <h3 className="font-bold text-[14px]">Document required</h3>
          <div
            className="border-2 border-dashed border-[#846CEC] h-[100px] sm:h-[120px] rounded-lg flex items-center justify-center cursor-pointer p-4"
            onClick={handleInput}
          >
            <div className="flex flex-col items-center text-center">
              <Upload color="#846CEC" className="w-5 h-5 sm:w-6 sm:h-6" />
              {uploadedDoc ? (
                <p className="font-normal text-[#202020] text-[12px] sm:text-[14px] mt-2">
                  {uploadedDoc.name}
                </p>
              ) : (
                <>
                  <p className="font-normal text-[#202020] text-[12px] sm:text-[14px] mt-2">
                    Drag and drop or click to choose the document
                  </p>
                  <p className="text-[10px] sm:text-[12px] font-light text-[#000a07]/60 mt-1">
                    PDF, DOC, DOCX (UP TO 20MB)
                  </p>
                </>
              )}
            </div>
          </div>
          {error && <p className="text-red-500 text-xs sm:text-sm">{error}</p>}
        </div>

        <div className="flex flex-row justify-end w-full gap-2 sm:gap-3 mt-2">
          <Button
            variant="customSecondary"
            size="sm"
            onClick={() => setchatwkbOpen(false)}
            className="text-[12px] sm:text-[14px] font-normal px-3 py-1.5"
          >
            Cancel
          </Button>

          <Button
            variant="customPrimary"
            size="sm"
            className="text-[12px] sm:text-[14px] font-normal px-3 py-1.5"
            type="button"
            onClick={() => handleSave(botId)}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader className="animate-spin w-4 h-4" />
            ) : (
              <span>Save</span>
            )}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default Chatwithkb;
